import React, { useState, useMemo, useRef, useEffect } from "react";
import Papa from "papaparse";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { useFileDrop } from "@/context/FileDropContext";
import { 
  FileText, 
  Upload, 
  RotateCcw, 
  Trash2, 
  Maximize2, 
  Minimize2, 
  Plus, 
  Search, 
  Download, 
  X, 
  Edit2, 
  Check, 
  ArrowRightLeft 
} from "lucide-react";

const SAMPLE_HEADERS = ["ID", "Name", "Department", "Email", "Salary", "Status"];
const SAMPLE_ROWS = [
  ["101", "Alice Johnson", "Engineering", "alice.j@company.com", "$115,000", "Active"],
  ["102", "Bob Smith", "Design", "bob.s@company.com", "$95,000", "Active"],
  ["103", "Charlie Brown", "Marketing", "charlie.b@company.com", "$85,000", "On Leave"],
  ["104", "Diana Prince", "Legal", "diana.p@company.com", "$145,000", "Active"],
  ["105", "Evan Wright", "Engineering", "evan.w@company.com", "$110,000", "Pending"],
];

export function CsvEditor() {
  const [headers, setHeaders] = useState<string[]>(SAMPLE_HEADERS);
  const [rows, setRows] = useState<string[][]>(SAMPLE_ROWS);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);
  const [jumpToRow, setJumpToRow] = useState("");
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [editingHeaderIdx, setEditingHeaderIdx] = useState<number | null>(null);
  const [headerEditValue, setHeaderEditValue] = useState("");
  const [feedback, setFeedback] = useState<{ type: "success" | "error" | ""; message: string }>({ type: "", message: "" });
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset page when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Filter rows based on search query
  const filteredRowsWithIndices = useMemo(() => {
    return rows
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return row.some(cell => cell.toLowerCase().includes(query));
      });
  }, [rows, searchQuery]);

  const totalPages = Math.ceil(filteredRowsWithIndices.length / pageSize) || 1;

  const paginatedRows = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredRowsWithIndices.slice(startIndex, startIndex + pageSize);
  }, [filteredRowsWithIndices, currentPage, pageSize]);

  // Handle cell edit
  const handleCellChange = (rowIndex: number, colIndex: number, val: string) => {
    const updated = [...rows];
    if (updated[rowIndex]) {
      updated[rowIndex][colIndex] = val;
      setRows(updated);
    }
  };

  // Add a blank row
  const handleAddRow = () => {
    const newRow = Array(headers.length).fill("");
    setRows([...rows, newRow]);
    showFeedback("success", "Added a new blank row.");
  };

  // Add a new column
  const handleAddColumn = () => {
    const colName = prompt("Enter new column name:") || `Column ${headers.length + 1}`;
    setHeaders([...headers, colName]);
    setRows(rows.map(row => [...row, ""]));
    showFeedback("success", `Added column "${colName}".`);
  };

  // Delete a column
  const handleDeleteColumn = (colIdx: number) => {
    if (headers.length <= 1) {
      showFeedback("error", "You must keep at least one column.");
      return;
    }
    if (confirm(`Are you sure you want to delete column "${headers[colIdx]}"?`)) {
      setHeaders(headers.filter((_, idx) => idx !== colIdx));
      setRows(rows.map(row => row.filter((_, idx) => idx !== colIdx)));
      showFeedback("success", "Column deleted successfully.");
    }
  };

  // Rename column
  const startHeaderEdit = (idx: number) => {
    setEditingHeaderIdx(idx);
    setHeaderEditValue(headers[idx]);
  };

  const saveHeaderEdit = (idx: number) => {
    if (!headerEditValue.trim()) return;
    const nextHeaders = [...headers];
    nextHeaders[idx] = headerEditValue.trim();
    setHeaders(nextHeaders);
    setEditingHeaderIdx(null);
    showFeedback("success", "Column renamed.");
  };

  // Delete a row
  const handleDeleteRow = (rowIdx: number) => {
    const updated = rows.filter((_, idx) => idx !== rowIdx);
    setRows(updated);
    showFeedback("success", "Row deleted.");
  };

  // Clear Grid
  const handleClearGrid = () => {
    if (confirm("Are you sure you want to clear the entire grid?")) {
      setHeaders(["ID", "Name"]);
      setRows([["", ""]]);
      showFeedback("success", "Grid cleared.");
    }
  };

  // Reset Sample
  const handleResetSample = () => {
    setHeaders(SAMPLE_HEADERS);
    setRows(SAMPLE_ROWS);
    showFeedback("success", "Reset grid to sample CSV data.");
  };

  const parseCSVFile = (file: File) => {
    Papa.parse(file, {
      skipEmptyLines: true,
      complete: (results) => {
        const data = results.data as string[][];
        if (data.length > 0) {
          const uploadedHeaders = data[0];
          const uploadedRows = data.slice(1);
          setHeaders(uploadedHeaders);
          setRows(uploadedRows.length > 0 ? uploadedRows : [Array(uploadedHeaders.length).fill("")]);
          showFeedback("success", `Loaded CSV with ${uploadedRows.length} rows and ${uploadedHeaders.length} columns.`);
        } else {
          showFeedback("error", "CSV file appears to be empty.");
        }
      },
      error: (error) => {
        showFeedback("error", `Failed to parse CSV: ${error.message}`);
      }
    });
  };

  // Consume globally dropped file
  const { consumeFile } = useFileDrop();
  useEffect(() => {
    const file = consumeFile();
    if (file && file.name.toLowerCase().endsWith(".csv")) {
      parseCSVFile(file);
    }
  }, [consumeFile]);

  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (file.name.toLowerCase().endsWith(".csv")) {
        parseCSVFile(file);
      } else {
        showFeedback("error", "Please upload a valid .csv file.");
      }
    }
  };

  // File Upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) parseCSVFile(file);
  };

  // Export CSV
  const handleExportCSV = () => {
    const csvContent = Papa.unparse({
      fields: headers,
      data: rows
    });
    
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "edited_spreadsheet.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showFeedback("success", "Spreadsheet successfully compiled and exported to CSV!");
  };

  const showFeedback = (type: "success" | "error", message: string) => {
    setFeedback({ type, message });
    setTimeout(() => {
      setFeedback({ type: "", message: "" });
    }, 4000);
  };

  return (
    <ToolPageLayout
      title="Online CSV Grid Editor"
      description="Edit CSV data in a responsive tabular spreadsheet. Clean interface, cell-by-cell live editing, row & column manipulation, and search filtering."
      h1="Online CSV Grid Editor"
      category="JSON / Data"
      categoryPath="/csv-editor"
      howToUse={[
        "Upload any CSV file or use the sample spreadsheet.",
        "Double click or hover-click on column headers to rename columns.",
        "Edit cell values directly by typing inside the table.",
        "Add, delete or search rows and columns in real-time.",
        "Click 'COMPILE & EXPORT CSV' to download your completed spreadsheet."
      ]}
      faq={[
        { question: "Is my data uploaded to a server?", answer: "No, all file parsing, grid rendering, and CSV exports happen locally inside your web browser. No server interaction or tracking is involved." },
        { question: "How can I filter rows?", answer: "Use the built-in search bar above the grid to find specific values instantly. Cell edits will still update the correct row automatically." },
        { question: "Can I add new columns?", answer: "Yes! Click the '+ Add Column' button, name your new column, and start populating cells instantly." }
      ]}
    >
      <div className={`space-y-6 ${isFullScreen ? "fixed inset-0 z-[9999] bg-slate-50 p-4 sm:p-6 overflow-y-auto" : "w-full"}`}>
        
        {/* Editor Wrapper Card */}
        <div 
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`bg-white border-2 rounded-2xl shadow-sm overflow-hidden flex flex-col transition-all ${
            isDragOver 
              ? "border-dashed border-red-500 bg-red-50/10 scale-[0.995]" 
              : "border-slate-200"
          }`}
        >
          
          {/* Header Controls */}
          <div className="p-5 border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-slate-50/50">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="p-1.5 bg-red-100 text-red-600 rounded">
                  <FileText className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">Online CSV Grid Editor</h3>
              </div>
              <p className="text-xs text-slate-500">Edit cells directly, filter rows, or manage columns and rows below in real-time.</p>
            </div>
            
            {/* Action buttons */}
            <div className="flex flex-wrap items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setIsFullScreen(!isFullScreen)}
                className="gap-1.5 text-slate-700 hover:text-slate-900"
              >
                {isFullScreen ? (
                  <>
                    <Minimize2 className="w-4 h-4" />
                    Exit Full Screen
                  </>
                ) : (
                  <>
                    <Maximize2 className="w-4 h-4" />
                    Full Screen
                  </>
                )}
              </Button>

              <input 
                type="file" 
                ref={fileInputRef} 
                onChange={handleFileUpload} 
                accept=".csv" 
                className="hidden" 
              />
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => fileInputRef.current?.click()}
                className="gap-1.5 bg-red-50 hover:bg-red-100 text-red-700 border-red-200 hover:border-red-300"
              >
                <Upload className="w-4 h-4" />
                Upload CSV File
              </Button>

              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleResetSample}
                className="gap-1.5 text-slate-700 hover:text-slate-900"
              >
                <RotateCcw className="w-4 h-4" />
                Reset Sample
              </Button>

              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleClearGrid}
                className="gap-1.5 text-red-600 hover:bg-red-50 border-transparent hover:border-red-200"
              >
                <Trash2 className="w-4 h-4" />
                Clear Grid
              </Button>
            </div>
          </div>

          {/* Search & Add controls */}
          <div className="p-4 border-b border-slate-100 bg-white flex flex-col sm:flex-row items-center gap-3">
            <div className="relative w-full sm:flex-1">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input 
                type="text"
                placeholder="Search spreadsheet rows..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-9 pr-4 py-2 text-sm outline-none focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
              />
              {searchQuery && (
                <button 
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            
            <div className="flex gap-2 w-full sm:w-auto shrink-0">
              <Button 
                onClick={handleAddRow}
                className="flex-1 sm:flex-none gap-1 bg-slate-900 hover:bg-slate-800 text-white border-none py-2 px-4 rounded-lg"
              >
                <Plus className="w-4 h-4" />
                Add Row
              </Button>
              <Button 
                onClick={handleAddColumn}
                variant="outline"
                className="flex-1 sm:flex-none gap-1 border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg"
              >
                <Plus className="w-4 h-4" />
                Add Column
              </Button>
            </div>
          </div>

          {/* Feedback Toast */}
          {feedback.message && (
            <div className={`px-5 py-3 border-b text-sm transition-all flex items-center justify-between ${
              feedback.type === "success" 
                ? "bg-emerald-50 text-emerald-800 border-emerald-100" 
                : "bg-red-50 text-red-800 border-red-100"
            }`}>
              <span className="font-medium">{feedback.message}</span>
              <button onClick={() => setFeedback({ type: "", message: "" })}>
                <X className="w-4 h-4 text-slate-400 hover:text-slate-600" />
              </button>
            </div>
          )}

          {/* Excel Grid Table Area */}
          <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
            <table className="w-full text-left border-collapse min-w-[800px]">
              <thead className="bg-slate-50 sticky top-0 z-10 border-b border-slate-200">
                <tr>
                  {/* Index Header */}
                  <th className="w-16 px-4 py-3 text-xs font-bold text-slate-500 uppercase text-center border-r border-slate-200">
                    #
                  </th>
                  
                  {/* Dynamic Column Headers */}
                  {headers.map((header, colIdx) => (
                    <th 
                      key={colIdx} 
                      className="px-4 py-3 text-sm font-bold text-slate-700 border-r border-slate-200 group relative select-none"
                    >
                      {editingHeaderIdx === colIdx ? (
                        <div className="flex items-center gap-1">
                          <input 
                            value={headerEditValue}
                            onChange={(e) => setHeaderEditValue(e.target.value)}
                            onBlur={() => saveHeaderEdit(colIdx)}
                            onKeyDown={(e) => e.key === "Enter" && saveHeaderEdit(colIdx)}
                            className="bg-white border border-blue-500 px-1.5 py-0.5 rounded text-sm w-full outline-none font-medium"
                            autoFocus
                          />
                          <button 
                            onClick={() => saveHeaderEdit(colIdx)}
                            className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                          >
                            <Check className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between gap-2">
                          <span 
                            onDoubleClick={() => startHeaderEdit(colIdx)}
                            className="truncate cursor-pointer hover:text-blue-600"
                            title="Double-click to rename"
                          >
                            {header}
                          </span>
                          <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5 transition-opacity">
                            <button 
                              onClick={() => startHeaderEdit(colIdx)}
                              className="p-1 hover:bg-slate-200 text-slate-400 hover:text-slate-600 rounded"
                              title="Rename Column"
                            >
                              <Edit2 className="w-3 h-3" />
                            </button>
                            {headers.length > 1 && (
                              <button 
                                onClick={() => handleDeleteColumn(colIdx)}
                                className="p-1 hover:bg-red-50 text-slate-400 hover:text-red-500 rounded"
                                title="Delete Column"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </th>
                  ))}
                  
                  {/* Action Column */}
                  <th className="w-20 px-4 py-3 text-xs font-bold text-slate-400 uppercase text-center">
                    Action
                  </th>
                </tr>
              </thead>
              
              <tbody className="divide-y divide-slate-100 bg-white">
                {paginatedRows.map(({ row, index: originalRowIndex }, rIdx) => (
                  <tr key={originalRowIndex} className="hover:bg-slate-50/50 group">
                    {/* Index row */}
                    <td className="px-4 py-2 text-xs font-medium text-slate-400 text-center bg-slate-50/50 border-r border-slate-100">
                      {originalRowIndex + 1}
                    </td>
                    
                    {/* Inline cell inputs */}
                    {row.map((cellValue, colIdx) => (
                      <td key={colIdx} className="p-1 border-r border-slate-100 min-w-[120px]">
                        <input 
                          type="text"
                          value={cellValue}
                          onChange={(e) => handleCellChange(originalRowIndex, colIdx, e.target.value)}
                          className="w-full bg-transparent hover:bg-slate-50 focus:bg-white border-0 focus:border focus:border-blue-500 px-3 py-1.5 text-sm rounded outline-none transition-all"
                        />
                      </td>
                    ))}
                    
                    {/* Row delete action */}
                    <td className="px-4 py-2 text-center">
                      <button 
                        onClick={() => handleDeleteRow(originalRowIndex)}
                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete Row"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Stats Bar and Export Footer */}
          <div className="p-5 border-t border-slate-100 bg-slate-50/50 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="text-sm text-slate-600 flex flex-wrap items-center gap-4">
              <span><span className="font-semibold">{filteredRowsWithIndices.length}</span> total rows active.</span>
              
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>First</Button>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Prev</Button>
                <span className="text-sm font-medium">Page {currentPage} of {totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next</Button>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>Last</Button>
              </div>

              <div className="flex items-center gap-2">
                <Input 
                  type="number" 
                  value={jumpToRow}
                  onChange={(e) => setJumpToRow(e.target.value)}
                  placeholder="Row #"
                  className="w-20 h-9"
                />
                <Button size="sm" onClick={() => {
                  const rowNum = parseInt(jumpToRow);
                  if (!isNaN(rowNum) && rowNum > 0 && rowNum <= filteredRowsWithIndices.length) {
                    setCurrentPage(Math.ceil(rowNum / pageSize));
                  }
                }}>Jump</Button>
              </div>
            </div>
            
            <Button 
              onClick={handleExportCSV}
              className="gap-2 bg-red-600 hover:bg-red-700 text-white border-none py-3 px-6 text-sm font-bold shadow-sm rounded-xl uppercase transition-colors shrink-0"
            >
              <Download className="w-4 h-4" />
              COMPILE & EXPORT CSV
            </Button>
          </div>
          
        </div>

      </div>
    </ToolPageLayout>
  );
}
